import type { ProductInfoData } from "@/components/ui/product-info-modal"
import { BLOOD_ADDONS } from "@/data/blood-addons"
import { ADDON_INFO, MRI_PACKAGE_INFO } from "@/data/product-info"
import {
  MEMBERSHIP_FIRST_ADDON_INFO,
  MEMBERSHIP_FIRST_BLOOD_ADDONS,
} from "@/data/membership-first-blood-addons"
import { blobMediaUrl } from "@/lib/blob-url"
import type { MriId } from "./pricing-v5-shared"

/**
 * What the builder shows beside each item and in its "More info" popup. The
 * popups read the same data as today's /pricing: `ADDON_INFO` and
 * `MRI_PACKAGE_INFO` (their `cmsPackageId` names the CMS biomarkers). Their
 * pictures are the new booking's: the tubes, the scan art and the scanner
 * renders, on Blob under `images/addons/`. Only the ground of the plan mark
 * is this page's own, copied from the booking's
 * `src/assets/images/membership-first/` to Blob under `images/pricing-v5/`.
 */

/** How a picture sits, as in the booking. */
export type PictureKind =
  /** A tube, whole, on white. */
  | "tube"
  /** An illustration filling its rounded plate. */
  | "photo"
  /** A scanner render shot on white, set on the card's own white. */
  | "render"

export type ItemDetails = {
  info?: ProductInfoData
  image?: string
  kind: PictureKind
  /**
   * The test page whose CMS row says whether the market can book it, as
   * ImagingAddonsSection gates the same tests. Blood tests have none.
   */
  gate?: string
}

/** The imaging tests' test pages, for their market gate. */
const IMAGING_GATES: Record<string, string> = {
  addonDEXA: "dexa-scan",
  addonHeartCT: "calcium-score",
  addonMammaMRI: "mamma-mri",
}

/** A single test's details, by its `pricing.addon*` key. */
export function testDetails(key: string): ItemDetails {
  // The membership-first site's own tests, a tube like every blood test.
  const own = MEMBERSHIP_FIRST_BLOOD_ADDONS.find((a) => a.titleKey === key)
  if (own)
    return {
      info: MEMBERSHIP_FIRST_ADDON_INFO[key],
      image: own.imageSrc,
      kind: "tube",
    }
  const info = ADDON_INFO[key]
  const tube = BLOOD_ADDONS.find((addon) => addon.titleKey === key)
  if (tube) return { info, image: tube.membershipFirstImageSrc, kind: "tube" }
  return {
    info,
    image: info?.membershipFirstImage ?? info?.image,
    kind: "photo",
    gate: IMAGING_GATES[key],
  }
}

/** An MRI package's details. Both are the full-body MRI's service. */
export function mriDetails(id: MriId): ItemDetails {
  const info =
    MRI_PACKAGE_INFO[
      id === "core" ? "Core" : id === "advanced" ? "Advanced" : "Pro"
    ]
  return {
    info,
    image: info?.membershipFirstImage ?? info?.image,
    kind: "render",
    gate: "full-body-mri",
  }
}

/** The plan's blood test: its biomarkers and facts. */
export const PLAN_TEST = ADDON_INFO.advancedBloodTest

/** The ground of the booking's plan mark, the white Ahead arrow on it. */
export const PLAN_MARK_BG = blobMediaUrl(
  "/images/pricing-v5/membership-logo-bg.webp"
)
